import { formatNumber } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';
import { Material } from '../classes/material.class';
import { RawMaterial } from '../classes/raw-material.class';

export type MaterialProperty =
    | 'name'
    | 'type'
    | 'supplier'
    | 'color'
    | 'colorName'
    | 'colorRal'
    | 'typeName'
    | 'certificate_number'
    | 'thickness'
    | 'consumption'
    | 'width'
    | 'usage'
    | 'quantity_unit';

const getMainRawMaterial = (material?: Material | null) => material?.raw_materials?.find(rm => rm.material_type?.main);

const hasUsableColor = (rm?: RawMaterial | null) => !!rm?.color?.color;

// Kleuren die bewust geen vaste hex hebben maar NIET naar een submateriaal moeten afleiden.
const NON_DERIVING_COLOR_NAMES = ['gekleurd'];

// Shared with MaterialDetailsPipe en MaterialColorPipe: welke grondstof levert de te tonen kleur.
// Normaal hoofdmateriaal, maar als dat transparant is, wordt de eerste kleur getoond
export function getColorSourceRawMaterial(material?: Material | null): RawMaterial | undefined {
    const mainRawMaterial = getMainRawMaterial(material);

    if (hasUsableColor(mainRawMaterial) || NON_DERIVING_COLOR_NAMES.includes(mainRawMaterial?.color?.name)) {
        return mainRawMaterial;
    }

    return material?.raw_materials?.find(rm => rm !== mainRawMaterial && hasUsableColor(rm)) ?? mainRawMaterial;
}

// Shared with day-report-materialen.component.ts, which needs the raw rate (not the formatted
// label) to calculate a total kg consumption across dayreport lines.
export function getMaterialConsumptionRate(material?: Material | null): number | undefined {
    const mainRawMaterial = getMainRawMaterial(material);

    return mainRawMaterial?.pivot?.consumption ?? mainRawMaterial?.consumption;
}

// Shared with production-table.service.ts, which needs the same "1,2 kg/m²" label outside of a
// template context (it builds column headers, not bindings).
export function getMaterialUsageLabel(material?: Material | null): string {
    const usage = getMaterialConsumptionRate(material);

    return usage ? `${formatNumber(usage, 'nl')} ${getMainRawMaterial(material)?.consumption_type?.name}` : '';
}

// Shared with behoefte-column.util.ts, which needs the raw kg/m² value (not the formatted NL
// string from getMaterialProperty(..., 'consumption')) to threshold streep vs. figuratie.
export function getMaterialConsumptionValue(material?: Material | null): number | null {
    const mainRawMaterial = getMainRawMaterial(material);
    const consumption = mainRawMaterial?.pivot?.consumption ?? mainRawMaterial?.consumption;

    return consumption != null ? Number(consumption) : null;
}

// Shared with MaterialPropertiesPipe, which applies this to a list of materials instead of one.
export function getMaterialProperty(
    material: Material | null,
    property: MaterialProperty,
    type?: string | null
): string {
    if (!material) {
        return '';
    }

    const mainRawMaterial = getMainRawMaterial(material);
    const color = getColorSourceRawMaterial(material)?.color;

    switch (property) {
        case 'name':
            return material.name || '';

        case 'type':
            return mainRawMaterial?.material_type?.name || '';

        case 'usage':
            return getMaterialUsageLabel(material);

        case 'thickness':
            return formatNumber(mainRawMaterial?.thickness, 'nl') || '';

        case 'consumption':
            let consumption = mainRawMaterial?.pivot?.consumption ?? mainRawMaterial?.consumption;
            return consumption ? formatNumber(consumption, 'nl') : '';

        case 'width':
            return mainRawMaterial?.width && mainRawMaterial.width !== 0
                ? formatNumber(mainRawMaterial.width, 'nl')
                : null;

        case 'quantity_unit':
            return (mainRawMaterial?.pivot?.consumption ?? mainRawMaterial?.consumption)
                ? 'kg'
                : mainRawMaterial?.width
                  ? 'm'
                  : '';

        case 'typeName':
            return `${material.name}${type !== material.name ? ` ${type}` : ''}`;

        case 'supplier':
            return mainRawMaterial?.supplier?.name || '';

        case 'certificate_number':
            return mainRawMaterial?.certificate_number || '';

        case 'color':
            return color?.color || '';

        case 'colorName':
            return color?.name ?? '';

        case 'colorRal':
            return color?.ral || '';

        default:
            return '';
    }
}

@Pipe({
    name: 'materialProperty',
    standalone: false,
})
export class MaterialPropertyPipe implements PipeTransform {
    transform(material: Material | null, property: MaterialProperty, type?: string | null): string {
        return getMaterialProperty(material, property, type);
    }
}
